import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Package, PencilRuler, Boxes, Tag,
  AlertTriangle, Trash2, CheckCircle2, Check, Paperclip, X, Mail, MessageCircle, Plus, Info
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { pimg, SITE, formatCategoryName } from "../lib/site";
import { useInquiry } from "../context/InquiryContext";
import { useSettings } from "../context/SettingsContext";
import { EASE } from "../components/motion/Reveal";
import PageMeta from "../components/PageMeta";

const TYPES = [
  { id: "product", icon: Package, label: "Product", desc: "Order from the catalog" },
  { id: "custom", icon: PencilRuler, label: "Custom Product", desc: "Built to your spec" },
  { id: "bulk", icon: Boxes, label: "Bulk Order", desc: "Wholesale volumes" },
  { id: "private-label", icon: Tag, label: "Private Label", desc: "Your brand, our factory" },
];

const STEP_LABELS = ["Offering", "Product", "Requirements", "Details", "Review"];
const CUSTOMIZATION_OPTIONS = [
  "Custom Sublimation / Printing",
  "Embroidery / 3D Silicone Logo",
  "Private Label / Woven Neck Tags",
  "Custom Polybag & Hangtags",
  "Custom Pantone Fabric Dyeing",
  "Reflective Heat-Transfer Accents",
];

const APPAREL_SIZES = [
  "Full Size Breakdown (XS - 3XL)",
  "Standard Adult Run (S, M, L, XL, 2XL)",
  "Womens Athletic Fit (XS, S, M, L, XL)",
  "Youth / Junior Run (YXS, YS, YM, YL, YXL)",
  "Custom Spec / Tech Pack (details/instructions in special instructions field below)",
  "Custom Size Breakdown (details/instructions in special instructions field below)",
  "Standard (16mm Paddle / One Size Gear)",
  "Standard (13mm Paddle / One Size Gear)",
];

const FABRIC_COLORS = [
  "Custom Pantone (details/instructions in special instructions field below)",
  "Custom Color Code (details/instructions in special instructions field below)",
  "All-Over Sublimation Print (Custom Artwork)",
  "Solid Black (Reactive Dye)",
  "Optic White",
  "Navy Blue (Deep Navy)",
  "Heather Grey Melange",
  "Royal Blue",
  "Pickleball Neon Volt / Lime",
  "Forest Green",
  "Two-Tone Colorblocking",
];

const FABRIC_MATERIALS = [
  "100% Micro-Polyester Moisture-Wicking (140-160 GSM)",
  "Polyester / Spandex 4-Way Stretch (88/12 - 200 GSM)",
  "Interlock Dry-Fit Mesh (180 GSM)",
  "French Terry Cotton / Poly Fleece (280-320 GSM)",
  "Jacquard Breathable Pique Fabric",
  "Raw Carbon T700 (For Paddles)",
  "Heavy-Duty 600D Cordura (For Bags)",
  "Custom Fabric Blend / GSM Spec (details/instructions in special instructions field below)",
];

const stepAnim = {
  initial: { opacity: 0, x: 48 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -48 },
  transition: { duration: 0.45, ease: EASE },
};

const Inquiry = () => {
  const [params] = useSearchParams();
  const preProduct = params.get("product") || "";
  const preType = params.get("type") || "";
  const { items: cartItems, removeItem, updateQuantity, clear } = useInquiry();
  const site = useSettings();

  const [products, setProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Products");
  const [step, setStep] = useState(preProduct ? 3 : preType ? 2 : 1);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState("");
  const [batchQtyInput, setBatchQtyInput] = useState("");

  const [form, setForm] = useState({
    type: preType || (preProduct ? "product" : ""),
    category: "",
    productId: preProduct,
    productIds: preProduct ? [preProduct] : [],
    quantities: preProduct ? { [preProduct]: "" } : {},
    quantity: "",
    size: "Full Size Breakdown (XS - 3XL)",
    customSize: "",
    color: "Custom Pantone (details/instructions in special instructions field below)",
    customColor: "",
    material: "100% Micro-Polyester Moisture-Wicking (140-160 GSM)",
    customMaterial: "",
    customization: [],
    instructions: "",
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    files: [],
  });

  useEffect(() => {
    api.get("/categories")
      .then((r) => {
        if (Array.isArray(r.data)) setApiCategories(r.data);
      })
      .catch(() => {});

    api.get("/products")
      .then((r) => {
        if (Array.isArray(r.data)) {
          setProducts(r.data);
          // Initialize preselected product quantity with its MOQ
          if (preProduct) {
            const match = r.data.find((p) => p.id === preProduct);
            if (match) {
              setForm((prev) => ({
                ...prev,
                quantity: prev.quantity || match.moq || 100,
                quantities: {
                  ...prev.quantities,
                  [preProduct]: prev.quantities[preProduct] || match.moq || 100,
                },
              }));
            }
          }
        }
      })
      .catch(() => {});
  }, [preProduct]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("files", [...form.files, { path: r.data.path, filename: r.data.filename }]);
      toast.success(`${f.name} attached`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Extract ONLY parent categories
  const parentCategories = useMemo(() => {
    const list = [];
    const seen = new Set();
    if (Array.isArray(apiCategories) && apiCategories.length > 0) {
      const isParent = (c) => !c.parent_id || c.parent_id === 0 || c.parent_id === "0" || c.parent_id === "null";
      const parents = apiCategories
        .filter(isParent)
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || Number(a.id) - Number(b.id));

      parents.forEach((c) => {
        const formatted = formatCategoryName(c.name);
        const lower = formatted.toLowerCase();
        if (!seen.has(lower)) {
          seen.add(lower);
          list.push({ id: c.id, name: formatted, raw: c });
        }
      });
    } else {
      products.forEach((p) => {
        const cat = p.parent_category || p.category;
        if (cat) {
          const formatted = formatCategoryName(cat);
          const lower = formatted.toLowerCase();
          if (!seen.has(lower)) {
            seen.add(lower);
            list.push({ id: formatted, name: formatted, raw: null });
          }
        }
      });
    }
    return list;
  }, [apiCategories, products]);

  // Filter products by selected parent category (or All Products)
  const categoryProducts = useMemo(() => {
    if (!selectedCategory || selectedCategory.toLowerCase() === "all" || selectedCategory.toLowerCase() === "all products") {
      return products;
    }
    const parent = parentCategories.find((c) => c.name.toLowerCase() === selectedCategory.toLowerCase());
    if (!parent || !Array.isArray(apiCategories) || apiCategories.length === 0) {
      return products.filter((p) => formatCategoryName(p.parent_category || p.category).toLowerCase() === selectedCategory.toLowerCase());
    }
    const childCategoryNames = new Set([parent.name.toLowerCase(), (parent.raw?.name || "").toLowerCase()]);
    const childCategoryIds = new Set([Number(parent.id)]);
    apiCategories.forEach((cat) => {
      if (cat.parent_id != null && Number(cat.parent_id) === Number(parent.id)) {
        childCategoryIds.add(Number(cat.id));
        if (cat.name) {
          childCategoryNames.add(cat.name.toLowerCase());
          childCategoryNames.add(formatCategoryName(cat.name).toLowerCase());
        }
      }
    });
    return products.filter((p) => {
      const pCat = (p.category || "").toLowerCase();
      const pParentCat = (p.parent_category || "").toLowerCase();
      const pCatFmt = formatCategoryName(p.category || "").toLowerCase();
      const pCatId = Number(p.category_id);
      const pParentId = Number(p.parent_category_id);
      return (
        childCategoryNames.has(pCat) ||
        childCategoryNames.has(pParentCat) ||
        childCategoryNames.has(pCatFmt) ||
        childCategoryIds.has(pCatId) ||
        childCategoryIds.has(pParentId)
      );
    });
  }, [products, selectedCategory, parentCategories, apiCategories]);

  // Toggle multi-select product
  const toggleProduct = (pid) => {
    setForm((f) => {
      const exists = f.productIds.includes(pid);
      const newIds = exists ? f.productIds.filter((id) => id !== pid) : [...f.productIds, pid];
      const newQuantities = { ...f.quantities };
      if (!exists && (!newQuantities[pid] || newQuantities[pid] <= 0)) {
        const prod = products.find((p) => p.id === pid);
        newQuantities[pid] = prod?.moq || 100;
      }
      return {
        ...f,
        productIds: newIds,
        productId: newIds[0] || "",
        quantities: newQuantities,
      };
    });
  };

  // Update specific product quantity with live validation
  const updateProductQty = (pid, val) => {
    if (val === "") {
      setForm((f) => ({
        ...f,
        quantities: { ...f.quantities, [pid]: "" },
      }));
      return;
    }
    const num = parseInt(val, 10);
    setForm((f) => ({
      ...f,
      quantities: {
        ...f.quantities,
        [pid]: isNaN(num) ? "" : num,
      },
    }));
  };

  // Handle onBlur to prevent quantity remaining below MOQ or empty
  const handleQtyBlur = (pid, minMoq, prodName) => {
    const currentVal = form.quantities[pid];
    const num = parseInt(currentVal, 10);
    if (isNaN(num) || num < minMoq) {
      setForm((f) => ({
        ...f,
        quantities: {
          ...f.quantities,
          [pid]: minMoq,
        },
      }));
      toast.warning(`Quantity for ${prodName} reset to minimum MOQ of ${minMoq} units`);
    }
  };

  // Apply batch quantity to all selected products (respecting each product's minimum MOQ)
  const applyBatchQty = () => {
    const num = parseInt(batchQtyInput, 10);
    if (isNaN(num) || num <= 0) {
      toast.error("Please enter a valid quantity greater than 0");
      return;
    }
    setForm((f) => {
      const updated = { ...f.quantities };
      f.productIds.forEach((pid) => {
        const prod = products.find((p) => p.id === pid);
        const minMoq = prod?.moq || 1;
        // Clamp to at least the product's MOQ
        updated[pid] = Math.max(num, minMoq);
      });
      return {
        ...f,
        quantity: num,
        quantities: updated,
      };
    });
    toast.success(`Updated quantities for all selected products (minimum MOQs enforced)`);
  };

  const selectedProducts = useMemo(
    () => products.filter((p) => form.productIds.includes(p.id)),
    [products, form.productIds]
  );
  const primaryProduct = selectedProducts[0] || null;

  // Compute total units across all selected products
  const totalUnits = useMemo(() => {
    if (selectedProducts.length === 0) return Number(form.quantity) || 0;
    return selectedProducts.reduce((sum, p) => {
      const q = Number(form.quantities[p.id]) || Number(form.quantity) || p.moq || 100;
      return sum + q;
    }, 0);
  }, [selectedProducts, form.quantities, form.quantity]);

  // Check which products are below their individual MOQ or 0
  const invalidMoqItems = useMemo(() => {
    return selectedProducts.filter((p) => {
      const q = form.quantities[p.id];
      const num = Number(q);
      const minMoq = p.moq || 1;
      return !num || num < minMoq;
    });
  }, [selectedProducts, form.quantities]);

  const tierPrice = (p, q) =>
    p?.price_tiers?.length
      ? p.price_tiers.reduce((best, t) => (q >= t.min_qty ? t.price : best), p.price_tiers[0].price)
      : null;

  // Custom condition checks
  const isCustomMaterial =
    form.material.includes("Custom") ||
    form.material.includes("details/instructions");

  const isCustomSize =
    form.size.includes("Custom") ||
    form.size.includes("details/instructions") ||
    form.size.toLowerCase().includes("spec");

  const isCustomColor =
    form.color.includes("Pantone") ||
    form.color.includes("Custom") ||
    form.color.includes("details/instructions");

  const canNext = () => {
    if (step === 1) return !!form.type;
    if (step === 2) return form.type === "product" ? form.productIds.length > 0 : true;
    if (step === 3) {
      if (selectedProducts.length > 0) {
        return invalidMoqItems.length === 0;
      }
      const generalQty = Number(form.quantity);
      return !isNaN(generalQty) && generalQty > 0;
    }
    if (step === 4)
      return form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim() && form.country.trim();
    return true;
  };

  const next = () => {
    if (step === 2 && form.type === "product" && form.productIds.length === 0) {
      toast.error("Please select at least one product to continue");
      return;
    }

    if (step === 3) {
      if (selectedProducts.length > 0 && invalidMoqItems.length > 0) {
        const firstInvalid = invalidMoqItems[0];
        toast.error(
          `Minimum order quantity for "${firstInvalid.name}" is ${firstInvalid.moq} ${firstInvalid.moq_unit || "pieces"}. Quantity cannot be below MOQ or 0.`
        );
        return;
      }
      if (selectedProducts.length === 0 && (!Number(form.quantity) || Number(form.quantity) <= 0)) {
        toast.error("Please specify a target quantity greater than 0");
        return;
      }
    }

    if (!canNext()) {
      toast.error("Please complete all required fields correctly");
      return;
    }

    setStep((s) => Math.min(5, s + 1));
    window.scrollTo(0, 0);
  };

  const submit = async () => {
    setSubmitting(true);
    const resolvedMaterial = isCustomMaterial && form.customMaterial.trim()
      ? `${form.material} — ${form.customMaterial.trim()}`
      : form.material;

    const resolvedSize = isCustomSize && form.customSize.trim()
      ? `${form.size} — ${form.customSize.trim()}`
      : form.size;

    const resolvedColor = isCustomColor && form.customColor.trim()
      ? `${form.color} — ${form.customColor.trim()}`
      : form.color;

    const items = [];
    selectedProducts.forEach((p) => {
      const pQty = Number(form.quantities[p.id]) || p.moq || 100;
      items.push({
        product_id: p.id,
        product_name: p.name,
        quantity: pQty,
        size: resolvedSize,
        color: resolvedColor,
        material: resolvedMaterial || p.material,
        customization: form.customization.join(", "),
        unit_price: tierPrice(p, pQty),
      });
    });

    cartItems.forEach((i) => {
      if (!form.productIds.includes(i.product_id)) {
        items.push(i);
      }
    });

    try {
      const r = await api.post("/inquiries", {
        inquiry_type: form.type || "product",
        items,
        customer: {
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          country: form.country,
        },
        requirements: {
          customization: form.customization,
          size: resolvedSize,
          color: resolvedColor,
          material: resolvedMaterial,
          instructions: form.instructions,
        },
        notes: form.instructions,
        files: form.files,
      });
      setReference(r.data.reference);
      clear();
      setStep(6);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-16 md:py-24 lg:px-10" data-testid="inquiry-page">
      <PageMeta
        title="Request a Factory Quote — Custom Pickleball Apparel & Gear"
        description="Submit a B2B inquiry for custom OEM/ODM pickleball apparel, paddles, and gear. Direct factory manufacturing from Sialkot, Pakistan."
      />
      <p className="mono-caption text-neutral-500">Inquiry Builder</p>
      <h1 className="mt-4 font-display text-4xl font-semibold tracking-tighter md:text-5xl">
        REQUEST A QUOTE<span className="text-brand-red">.</span>
      </h1>

      {step <= 5 && (
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 border-y border-black/10 py-4" data-testid="inquiry-progress">
          {STEP_LABELS.map((l, i) => (
            <span
              key={l}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] ${
                step === i + 1 ? "text-brand-red font-bold" : step > i + 1 ? "text-brand-ink" : "text-neutral-300"
              }`}
            >
              0{i + 1} {l}
            </span>
          ))}
        </div>
      )}

      <div className="mt-12 min-h-[380px]">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="s1" {...stepAnim}>
              <h2 className="font-display text-2xl font-medium tracking-tight">What are you looking for?</h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {TYPES.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`inquiry-type-option-${t.id}`}
                    onClick={() => set("type", t.id)}
                    className={`flex items-start gap-4 border p-6 text-left transition-colors ${
                      form.type === t.id ? "border-brand-red bg-brand-red/5" : "border-black/15 hover:border-brand-ink"
                    }`}
                  >
                    <t.icon size={24} className="mt-1 text-brand-red" />
                    <span>
                      <span className="block font-display text-xl font-medium">{t.label}</span>
                      <span className="mt-1 block text-sm text-neutral-500">{t.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" {...stepAnim}>
              <div className="flex flex-wrap items-baseline justify-between gap-4">
                <h2 className="font-display text-2xl font-medium tracking-tight">Select Products</h2>
                <div className="flex items-center gap-3">
                  {form.productIds.length > 0 && (
                    <span className="font-mono text-xs font-semibold text-brand-red bg-brand-red/10 px-2.5 py-1 border border-brand-red/20">
                      {form.productIds.length} {form.productIds.length === 1 ? "product" : "products"} selected
                    </span>
                  )}
                  {form.productIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, productIds: [], productId: "", quantities: {} }))}
                      className="font-mono text-[11px] text-neutral-400 hover:text-brand-red underline"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* Only 'All Products' tab and Parent Categories */}
              <div className="mt-6 flex flex-wrap gap-2" data-testid="inquiry-categories-tabs">
                <button
                  type="button"
                  data-testid="inquiry-cat-all"
                  onClick={() => setSelectedCategory("All Products")}
                  className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                    selectedCategory.toLowerCase() === "all products" || selectedCategory.toLowerCase() === "all"
                      ? "border-brand-ink bg-brand-ink text-white"
                      : "border-black/15 text-neutral-500 hover:border-brand-ink"
                  }`}
                >
                  All Products
                </button>
                {parentCategories.map((c) => (
                  <button
                    key={c.id || c.name}
                    type="button"
                    data-testid={`inquiry-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setSelectedCategory(c.name)}
                    className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                      selectedCategory.toLowerCase() === c.name.toLowerCase()
                        ? "border-brand-ink bg-brand-ink text-white"
                        : "border-black/15 text-neutral-500 hover:border-brand-ink"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>

              {/* Products list with main image and multi-select */}
              <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="inquiry-products-grid">
                {categoryProducts.map((p) => {
                  const isSelected = form.productIds.includes(p.id);
                  const mainImage = p.images?.[0] || p.image;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`inquiry-product-${p.id}`}
                      onClick={() => toggleProduct(p.id)}
                      className={`group flex items-center gap-4 border p-4 text-left transition-all ${
                        isSelected
                          ? "border-brand-ink bg-brand-ink text-white shadow-sm"
                          : "border-black/15 bg-white hover:border-brand-ink hover:bg-brand-smoke"
                      }`}
                    >
                      <div className="relative h-16 w-16 sm:h-20 sm:w-20 shrink-0 overflow-hidden border border-black/10 bg-neutral-100">
                        {mainImage ? (
                          <img
                            src={pimg(mainImage, 160, 160)}
                            alt={p.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-neutral-300">
                            <Package size={24} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="block font-display text-base sm:text-lg font-medium leading-snug line-clamp-2">
                          {p.name}
                        </span>
                        <span className={`mt-1 block font-mono text-[11px] uppercase tracking-wider ${
                          isSelected ? "text-brand-red font-semibold" : "text-neutral-400"
                        }`}>
                          MOQ {p.moq} {p.moq_unit || "pieces"}
                        </span>
                      </div>
                      <div className="shrink-0 pl-1">
                        {isSelected ? (
                          <div className="flex h-6 w-6 items-center justify-center bg-brand-red text-white">
                            <Check size={14} strokeWidth={3} />
                          </div>
                        ) : (
                          <div className="h-6 w-6 border border-black/20 group-hover:border-brand-ink transition-colors" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {form.type !== "product" && (
                <p className="mt-4 text-sm text-neutral-400">Optional — skip if you'd like our team to recommend.</p>
              )}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" {...stepAnim}>
              <div className="flex flex-wrap items-baseline justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-medium tracking-tight">Specifications & Quantities</h2>
                  <p className="mt-1 text-sm text-neutral-500">
                    Configure custom quantities per product, fabric constructions, and Pantone specifications.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  data-testid="inquiry-add-more-products"
                  className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-brand-red hover:underline"
                >
                  <Plus size={13} /> Add / Change Products
                </button>
              </div>

              {/* Dynamic Per-Product Quantities Section */}
              {selectedProducts.length > 0 && (
                <div className="mt-6 space-y-4" data-testid="inquiry-selected-products-list">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/10 pb-3">
                    <div className="flex items-center gap-2">
                      <span className="mono-caption text-neutral-500">
                        Selected Products ({selectedProducts.length})
                      </span>
                      <span className="font-mono text-xs font-bold text-brand-ink bg-brand-smoke px-2 py-0.5 border border-black/10">
                        Total: {totalUnits.toLocaleString()} units
                      </span>
                    </div>

                    {/* Batch Quantity Shortcut */}
                    {selectedProducts.length > 1 && (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[11px] text-neutral-400">Apply to all (min enforced):</span>
                        <input
                          type="number"
                          min="1"
                          placeholder="e.g. 500"
                          value={batchQtyInput}
                          onChange={(e) => setBatchQtyInput(e.target.value)}
                          className="h-7 w-20 border border-black/20 px-2 font-mono text-xs outline-none focus:border-brand-ink"
                        />
                        <button
                          type="button"
                          onClick={applyBatchQty}
                          className="h-7 border border-brand-ink bg-brand-ink px-2.5 font-mono text-[10px] uppercase tracking-wider text-white hover:bg-brand-red transition-colors"
                        >
                          Set
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Individual Product Quantity Cards */}
                  <div className="grid gap-3 sm:grid-cols-2">
                    {selectedProducts.map((sp) => {
                      const rawQty = form.quantities[sp.id];
                      const itemQty = Number(rawQty);
                      const minMoq = sp.moq || 1;
                      const isItemBelowMoq = !itemQty || itemQty < minMoq;
                      const itemTierPrice = tierPrice(sp, itemQty || minMoq);

                      return (
                        <div
                          key={sp.id}
                          className={`flex flex-col justify-between border p-4 transition-colors ${
                            isItemBelowMoq
                              ? "border-brand-red bg-brand-red/5 ring-1 ring-brand-red/30"
                              : "border-black/10 bg-white"
                          }`}
                          data-testid={`inquiry-selected-product-${sp.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <img
                              src={pimg(sp.images?.[0] || sp.image, 120, 120)}
                              alt={sp.name}
                              className="h-14 w-14 shrink-0 object-cover border border-black/10 bg-white"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-display text-base font-medium">{sp.name}</p>
                              <p className="font-mono text-[11px] uppercase tracking-wider text-neutral-400">
                                {formatCategoryName(sp.category)} · <strong className="text-brand-ink">MOQ {sp.moq} {sp.moq_unit || "pieces"}</strong>
                              </p>
                              {itemTierPrice != null && !isItemBelowMoq && (
                                <p className="mt-1 font-mono text-xs text-neutral-600">
                                  Est. <span className="font-semibold text-brand-ink">US ${itemTierPrice.toFixed(2)}</span>/pc
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleProduct(sp.id)}
                              aria-label={`Remove ${sp.name}`}
                              className="p-1 text-neutral-400 hover:text-brand-red transition-colors"
                            >
                              <X size={16} />
                            </button>
                          </div>

                          <div className="mt-3 flex items-center justify-between border-t border-black/5 pt-3">
                            <label className="font-mono text-[11px] uppercase tracking-wider text-neutral-500" htmlFor={`qty-${sp.id}`}>
                              Target Qty:
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                id={`qty-${sp.id}`}
                                type="number"
                                min={minMoq}
                                value={form.quantities[sp.id] ?? minMoq}
                                onChange={(e) => updateProductQty(sp.id, e.target.value)}
                                onBlur={() => handleQtyBlur(sp.id, minMoq, sp.name)}
                                className={`h-8 w-28 border px-2 text-right font-mono text-sm outline-none transition-colors ${
                                  isItemBelowMoq
                                    ? "border-brand-red bg-white text-brand-red font-bold ring-1 ring-brand-red"
                                    : "border-black/20 focus:border-brand-ink"
                                }`}
                              />
                              <span className="font-mono text-[11px] text-neutral-400">pcs</span>
                            </div>
                          </div>

                          {/* Explicit Error Message */}
                          {isItemBelowMoq && (
                            <div className="mt-2 flex items-start gap-1.5 border-t border-brand-red/20 pt-1.5">
                              <AlertTriangle size={13} className="shrink-0 text-brand-red mt-0.5" />
                              <p className="font-mono text-[11px] font-semibold text-brand-red leading-tight">
                                Minimum order quantity for this item is {sp.moq} {sp.moq_unit || "pieces"}. Quantity cannot be below MOQ or 0.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Single Product / General Quantity Fallback */}
              {selectedProducts.length === 0 && (
                <div className="mt-6">
                  <label className="label-pp" htmlFor="inq-qty">Target Total Quantity *</label>
                  <input
                    id="inq-qty"
                    type="number"
                    min="1"
                    value={form.quantity}
                    onChange={(e) => set("quantity", e.target.value)}
                    className="input-pp font-mono"
                    data-testid="inquiry-quantity"
                    placeholder="e.g. 500"
                  />
                  {(!Number(form.quantity) || Number(form.quantity) <= 0) && (
                    <p className="mt-1 font-mono text-xs text-brand-red">
                      ⚠️ Please specify a target quantity of at least 1 unit.
                    </p>
                  )}
                </div>
              )}

              {/* Specifications: Fabric, Size, Color */}
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                {/* Material / Fabric */}
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="label-pp" htmlFor="inq-material">Fabric & Material Construction</label>
                    {isCustomMaterial && (
                      <span className="font-mono text-[10px] text-brand-red font-semibold">
                        (details/instructions in special instructions field below)
                      </span>
                    )}
                  </div>
                  <select
                    id="inq-material"
                    value={form.material}
                    onChange={(e) => set("material", e.target.value)}
                    className="input-pp"
                    data-testid="inquiry-material"
                  >
                    {FABRIC_MATERIALS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>

                  {/* Dynamic Custom Material Helper */}
                  <AnimatePresence>
                    {isCustomMaterial && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 overflow-hidden border-l-2 border-brand-red bg-brand-red/5 p-3.5"
                      >
                        <label className="label-pp text-brand-ink" htmlFor="inq-custom-mat">
                          Custom Fabric Blend / GSM Spec Details
                        </label>
                        <input
                          id="inq-custom-mat"
                          value={form.customMaterial}
                          onChange={(e) => set("customMaterial", e.target.value)}
                          className="input-pp bg-white"
                          placeholder="e.g. 92% Polyester 8% Spandex, 220 GSM Brushed Backing, Anti-Pilling Finish"
                        />
                        <p className="mt-1.5 font-mono text-[10px] text-brand-red font-medium">
                          ℹ️ (details/instructions in special instructions field below)
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Size Dropdown */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="label-pp" htmlFor="inq-size">Size Profile / Breakdown</label>
                    {isCustomSize && (
                      <span className="font-mono text-[10px] text-brand-red font-semibold">
                        (details/instructions in special instructions field below)
                      </span>
                    )}
                  </div>
                  <select
                    id="inq-size"
                    value={form.size}
                    onChange={(e) => set("size", e.target.value)}
                    className="input-pp"
                    data-testid="inquiry-size"
                  >
                    {APPAREL_SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>

                  {/* Dynamic Custom Spec Field */}
                  <AnimatePresence>
                    {isCustomSize && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 overflow-hidden border-l-2 border-brand-red bg-brand-red/5 p-3.5"
                      >
                        <label className="label-pp text-brand-ink" htmlFor="inq-custom-size">
                          Custom Size Breakdown & Measurements
                        </label>
                        <input
                          id="inq-custom-size"
                          value={form.customSize}
                          onChange={(e) => set("customSize", e.target.value)}
                          className="input-pp bg-white"
                          placeholder="e.g. S: 50, M: 100, L: 150, XL: 100, 2XL: 50 (or Custom Spec Chart)"
                        />
                        <p className="mt-1.5 font-mono text-[10px] text-brand-red font-medium">
                          ℹ️ (details/instructions in special instructions field below)
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Color Dropdown */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="label-pp" htmlFor="inq-color">Color / Dyeing Method</label>
                    <a
                      href="/PANTONE.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[10px] text-brand-red underline hover:text-brand-reddark transition-colors inline-flex items-center gap-0.5"
                      data-testid="inquiry-pantone-link"
                    >
                      Pantone Guide ↗
                    </a>
                  </div>
                  <select
                    id="inq-color"
                    value={form.color}
                    onChange={(e) => set("color", e.target.value)}
                    className="input-pp"
                    data-testid="inquiry-color"
                  >
                    {FABRIC_COLORS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>

                  {/* Dynamic Custom Pantone Field */}
                  <AnimatePresence>
                    {isCustomColor && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 overflow-hidden border-l-2 border-brand-red bg-brand-red/5 p-3.5"
                      >
                        <label className="label-pp text-brand-ink" htmlFor="inq-custom-color">
                          Pantone TCX / TPX / Hex Color Codes
                        </label>
                        <input
                          id="inq-custom-color"
                          value={form.customColor}
                          onChange={(e) => set("customColor", e.target.value)}
                          className="input-pp bg-white font-mono"
                          placeholder="e.g. PANTONE 19-4052 TCX Classic Blue, PANTONE 18-1662 TPX Red"
                        />
                        <p className="mt-1.5 font-mono text-[10px] text-brand-red font-medium">
                          ℹ️ (details/instructions in special instructions field below)
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <p className="label-pp mt-8">Apparel & Customization Options</p>
              <div className="flex flex-wrap gap-2" data-testid="inquiry-customization">
                {CUSTOMIZATION_OPTIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`inquiry-cust-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() =>
                      set("customization", form.customization.includes(c)
                        ? form.customization.filter((x) => x !== c)
                        : [...form.customization, c])
                    }
                    className={`border px-4 py-2 text-xs transition-colors ${
                      form.customization.includes(c)
                        ? "border-brand-red bg-brand-red/5 text-brand-red font-medium"
                        : "border-black/15 text-neutral-500 hover:border-brand-ink"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <label className="label-pp" htmlFor="inq-notes">Special Instructions & Tech Pack Details</label>
                  <span className="font-mono text-[10px] text-brand-red">
                    Specify all custom fabric, size, and Pantone details here
                  </span>
                </div>
                <textarea
                  id="inq-notes"
                  rows={4}
                  value={form.instructions}
                  onChange={(e) => set("instructions", e.target.value)}
                  className="input-pp !h-auto py-3"
                  data-testid="inquiry-notes"
                  placeholder="Embroidery dimensions, printing technique (sublimation / screen print / DTF), custom polybag branding, target price, delivery deadlines, custom fabric specs, size breakdowns..."
                />
              </div>

              <div className="mt-6">
                <label className="label-pp">Attach Tech Pack / Vector Logo / Artwork (optional)</label>
                <label
                  className="flex cursor-pointer items-center gap-3 border border-dashed border-brand-ink/40 p-5 text-sm text-neutral-500 transition-colors hover:border-brand-red hover:text-brand-ink"
                  data-testid="inquiry-upload-dropzone"
                >
                  <Paperclip size={18} className="shrink-0 text-brand-red" />
                  {uploading ? "Uploading..." : "Click to attach PDF, AI, PNG, JPG, or Tech Pack (max 10MB)"}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.pdf,.ai"
                    className="hidden"
                    onChange={handleFile}
                    data-testid="inquiry-upload-input"
                  />
                </label>
                {form.files.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="inquiry-attachments">
                    {form.files.map((f, i) => (
                      <div key={f.path} className="flex items-center justify-between border border-black/10 px-4 py-2.5 font-mono text-xs">
                        <span className="flex items-center gap-2 truncate">
                          <Paperclip size={12} className="shrink-0 text-brand-red" /> {f.filename}
                        </span>
                        <button
                          type="button"
                          onClick={() => set("files", form.files.filter((x) => x.path !== f.path))}
                          data-testid={`inquiry-attachment-remove-${i}`}
                          aria-label="Remove attachment"
                          className="text-neutral-400 hover:text-brand-red"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="s4" {...stepAnim}>
              <h2 className="font-display text-2xl font-medium tracking-tight">Your Details</h2>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="label-pp" htmlFor="inq-name">Full Name *</label>
                  <input id="inq-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="input-pp" data-testid="inquiry-name" />
                </div>
                <div>
                  <label className="label-pp" htmlFor="inq-company">Company / Brand Name</label>
                  <input id="inq-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="input-pp" data-testid="inquiry-company" />
                </div>
                <div>
                  <label className="label-pp" htmlFor="inq-email">Business Email *</label>
                  <input id="inq-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-pp" data-testid="inquiry-email" />
                </div>
                <div>
                  <label className="label-pp" htmlFor="inq-phone">Phone / WhatsApp *</label>
                  <input id="inq-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-pp" data-testid="inquiry-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-pp" htmlFor="inq-country">Country / Destination Port *</label>
                  <input id="inq-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="input-pp" data-testid="inquiry-country" placeholder="e.g. USA (FOB / DDP)" />
                </div>
              </div>
            </motion.div>
          )}

          {step === 5 && (
            <motion.div key="s5" {...stepAnim}>
              <h2 className="font-display text-2xl font-medium tracking-tight">Review Your Inquiry</h2>
              <div className="mt-8 divide-y divide-black/10 border border-black/10" data-testid="inquiry-review">
                <div className="grid gap-1 p-5 sm:grid-cols-3">
                  <span className="mono-caption text-neutral-400">Inquiry Type</span>
                  <span className="font-medium sm:col-span-2">{TYPES.find((t) => t.id === form.type)?.label || "Product"}</span>
                </div>

                {selectedProducts.length > 0 && (
                  <div className="grid gap-1 p-5 sm:grid-cols-3">
                    <span className="mono-caption text-neutral-400">
                      Selected Items ({selectedProducts.length})<br />
                      <span className="text-xs font-normal text-neutral-500">Total: {totalUnits.toLocaleString()} units</span>
                    </span>
                    <div className="space-y-3 sm:col-span-2">
                      {selectedProducts.map((p) => {
                        const pQty = Number(form.quantities[p.id]) || p.moq || 100;
                        const unitPrice = tierPrice(p, pQty);
                        return (
                          <div key={p.id} className="flex items-center gap-3 border border-black/10 bg-brand-smoke/50 p-3">
                            <img
                              src={pimg(p.images?.[0] || p.image, 100, 100)}
                              alt={p.name}
                              className="h-12 w-12 shrink-0 object-cover border border-black/10 bg-white"
                            />
                            <div className="min-w-0 flex-1">
                              <span className="block font-medium truncate">{p.name}</span>
                              <span className="font-mono text-xs text-neutral-500">
                                Target Qty: <strong className="text-brand-ink">{pQty} pcs</strong>
                                {unitPrice != null && ` — US $${unitPrice.toFixed(2)}/pc indicative`}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid gap-1 p-5 sm:grid-cols-3">
                  <span className="mono-caption text-neutral-400">Fabric & Specs</span>
                  <span className="sm:col-span-2">
                    <span className="font-medium block">
                      {form.material} {form.customMaterial && `(${form.customMaterial})`}
                    </span>
                    <span className="text-xs text-neutral-500 block mt-1">
                      Size: {form.size} {form.customSize && `(${form.customSize})`}
                    </span>
                    <span className="text-xs text-neutral-500 block">
                      Color: {form.color} {form.customColor && `(${form.customColor})`}
                    </span>
                  </span>
                </div>

                {cartItems.filter((i) => !form.productIds.includes(i.product_id)).map((i) => (
                  <div key={i.product_id} className="grid items-center gap-1 p-5 sm:grid-cols-3">
                    <span className="mono-caption text-neutral-400">Also Inquiring</span>
                    <span className="font-medium">{i.product_name}</span>
                    <span className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        value={i.quantity}
                        onChange={(e) => updateQuantity(i.product_id, Number(e.target.value) || 1)}
                        className="h-9 w-24 border border-black/20 text-center font-mono text-sm outline-none"
                        data-testid={`review-qty-${i.product_id}`}
                      />
                      <button
                        onClick={() => removeItem(i.product_id)}
                        aria-label="Remove item"
                        data-testid={`review-remove-${i.product_id}`}
                        className="text-neutral-400 hover:text-brand-red"
                      >
                        <Trash2 size={16} />
                      </button>
                    </span>
                  </div>
                ))}

                {form.customization.length > 0 && (
                  <div className="grid gap-1 p-5 sm:grid-cols-3">
                    <span className="mono-caption text-neutral-400">Customization</span>
                    <span className="sm:col-span-2">{form.customization.join(", ")}</span>
                  </div>
                )}

                {form.instructions && (
                  <div className="grid gap-1 p-5 sm:grid-cols-3">
                    <span className="mono-caption text-neutral-400">Instructions</span>
                    <span className="sm:col-span-2 text-sm whitespace-pre-line">{form.instructions}</span>
                  </div>
                )}

                {form.files.length > 0 && (
                  <div className="grid gap-1 p-5 sm:grid-cols-3">
                    <span className="mono-caption text-neutral-400">Attachments</span>
                    <span className="flex flex-wrap gap-2 sm:col-span-2" data-testid="review-attachments">
                      {form.files.map((f) => (
                        <span key={f.path} className="inline-flex items-center gap-2 border border-black/15 px-3 py-1 font-mono text-xs">
                          <Paperclip size={11} className="text-brand-red" /> {f.filename}
                        </span>
                      ))}
                    </span>
                  </div>
                )}

                <div className="grid gap-1 p-5 sm:grid-cols-3">
                  <span className="mono-caption text-neutral-400">Payment Protocol</span>
                  <span className="sm:col-span-2 text-xs leading-relaxed text-neutral-600">
                    <strong className="text-brand-ink">50% advance deposit</strong> on official PI confirmation · <strong className="text-brand-ink">50% balance before dispatch</strong> upon QC inspection.<br />
                    <span className="text-neutral-500">Accepted: Bank Wire (T/T), Western Union, MoneyGram, L/C at Sight, Wise, Payoneer, PayPal.</span>{" "}
                    <Link to="/terms" target="_blank" className="text-brand-red underline hover:text-brand-reddark ml-1">
                      View Terms & Conditions ↗
                    </Link>
                  </span>
                </div>

                <div className="grid gap-1 p-5 sm:grid-cols-3">
                  <span className="mono-caption text-neutral-400">Contact</span>
                  <span className="sm:col-span-2">
                    {form.name}{form.company && ` — ${form.company}`}<br />
                    <span className="text-sm text-neutral-500">{form.email} / {form.phone} / {form.country}</span>
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {step === 6 && (
            <motion.div key="s6" {...stepAnim} className="py-16 text-center" data-testid="inquiry-success">
              <CheckCircle2 size={56} className="mx-auto text-brand-red" />
              <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight md:text-4xl">
                Inquiry Submitted<span className="text-brand-red">.</span>
              </h2>
              <p className="mt-4 text-neutral-500">
                Reference <span className="font-mono font-bold text-brand-ink" data-testid="inquiry-reference">{reference}</span>
              </p>
              <p className="mx-auto mt-2 max-w-md text-neutral-500">
                Our export team will review your quantities and fabric specifications and respond with a detailed quotation within 24 hours.
              </p>

              {/* Email + WhatsApp quick-contact buttons */}
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <a
                  href={`mailto:${site.email}?subject=RE:%20Inquiry%20${reference}&body=Hi%20Pickleballgearz%20team%2C%20I%20am%20following%20up%20on%20my%20inquiry%20${reference}.`}
                  className="btn-outline"
                  data-testid="success-email"
                >
                  <Mail size={15} /> Email Us
                </a>
                <a
                  href={`https://wa.me/${(site.phone || "").replace(/[^\d]/g, "")}?text=Hi%20Pickleballgearz%2C%20I%27m%20following%20up%20on%20my%20inquiry%20${encodeURIComponent(reference)}.%20Please%20let%20me%20know%20the%20status.`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-green"
                  data-testid="success-whatsapp"
                >
                  <MessageCircle size={15} /> WhatsApp
                </a>
              </div>

              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <Link to="/products" className="btn-dark" data-testid="success-browse">Browse Products</Link>
                <Link to="/" className="btn-outline" data-testid="success-home">Back Home</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {step <= 5 && (
        <div className="mt-12 flex items-center justify-between border-t border-black/10 pt-8">
          <button
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            data-testid="inquiry-back-btn"
            className="btn-outline disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ArrowLeft size={16} /> Back
          </button>
          {step < 5 ? (
            <button onClick={next} data-testid="inquiry-next-btn" className="btn-primary">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="inquiry-submit-btn" className="btn-primary disabled:opacity-50">
              {submitting ? "Submitting..." : "Submit Inquiry"} <ArrowRight size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Inquiry;
